<template>
  <div class="container-fluid page-container pb-5 h-100">
    <div class="row h-100">
      <div class="col-12 col-md-8 d-flex flex-column h-100">
        <home-message-block
          v-if="displayJumbo"
          ref="homeMessages"
          :show-validate="requiresEmailValidation"
          :validation-resend-timer="timeLeftTillCanSendValidation"
          :company-user="companyUser"
          :company-created="companyCreated"
          :company-status="companyStatus"
          class="pb-md-3"
          @sendToken="onSendToken"
        />
        <div class="row">
          <div v-if="isDesktop && showBuyLeads" class="col">
            <h2 class="h3 pt-4 pb-2">
              {{ $t('Latest Leads') }}
            </h2>
            <b-card ref="buyLeadsCard" no-body class="table-wrap">
              <marketplace-leads-table-lite
                ref="buyLeadsTable"
                :display-rows="buyLeadsRows"
              />
            </b-card>
          </div>

          <home-buy-leads-mobile
            v-else-if="isTablet && showBuyLeads"
            class="col-12 pt-3"
            :buy-leads-rows="buyLeadsRows"
          />
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="row">
          <div v-if="isMobile" ref="todoList" class="col-12 order-1 order-md-2 mb-2">
            <notification-accordion :todos="todos" :todos-selected="$route.hash === '#todo'" />
          </div>
          <div v-else class="col-12 order-1 order-md-0">
            <h2 class="h3 pt-md-0 pt-4 pb-2 d-flex">
              <span class="line-height-5">{{ $t('Notices') }}</span> <spinner v-if="noticesBusy" small="true" class="ml-3" />
            </h2>
            <b-card no-body>
              <b-card-body class="p-3 border rounded shadow flex-grow-0">
                <homepage-notifications />
              </b-card-body>
            </b-card>
          </div>

          <div class="col-12 order-0 order-md-1 pb-md-3 pt-3 pt-md-0 pb-md-0 account__overview">
            <h2 class="d-none d-md-block h3 pt-4 pb-2">
              {{ $t('Account Overview') }}
            </h2>
            <b-card no-body>
              <b-card-body class="p-3 border shadow rounded flex-grow-0">
                <div class="row justify-content-between align-items-center">
                  <div
                    :class="['account__balance', 'col-4', 'col-md-12', 'col-xxl-4', 'd-flex', 'flex-column', 'text-center', {'pr-0': (accountBalance > 0)}]"
                  >
                    <span class="account__balance--currency h4 mb-0">{{ $n(accountBalance,'currency') }}
                      <small class="account__balance--text d-inline-block d-md-none">{{ $t('accountBalance.vat') }}</small>
                    </span>
                    <small class="mb-0 mb-xl-2 mb-xxl-0 account__balance--text">{{ $t('accountBalance.title') }}
                      <span class="d-none d-md-inline-block">{{ $t('accountBalance.vat') }}</span></small>
                  </div>

                  <div
                    v-b-tooltip.hover
                    class="account__settle col-4 col-md-12 col-xxl-4 py-md-2"
                    :title="accountBalance === 0 ? $t('Your account is settled. Thank you.') : ''"
                  >
                    <b-button
                      variant="primary"
                      class="px-1 py-2 py-md-2 line-height-1"
                      :disabled="accountBalance === 0"
                      :to="{path:'/account/invoices', query: {settleAccount: true}}"
                    >
                      {{ $t('Settle Account') }}
                    </b-button>
                  </div>

                  <div
                    :class="['order-1', 'col-xxl-4', 'text-center', reviewRatingClass]"
                  >
                    <router-link class="btn-empty" :to="{name: 'reviews'}">
                      <h2 v-if="reviewCount > 0" class="h4 text-center mb-1">
                        {{ reviewRating.toFixed(1) }}/5 <span class="ml-1 h5 font-weight-500">{{ $tc('reviews', reviewCount) }}</span>
                      </h2>
                      <small v-else class="account__review--text">{{ $t('No reviews yet') }}</small>
                      <b-form-rating class="border-0 b-rating-feedback m-auto p-0" :value="reviewRating ? reviewRating : 0" readonly />
                    </router-link>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>

          <div v-if="hasTodo && !isMobile && showSettings" class="col-12 order-3 order-md-2">
            <div class="row pt-4 pb-2">
              <h2 class="h3 col">
                {{ $t('To Do') }}
              </h2>
              <span class="col text-right small">
                {{ $tc('itemsCount', Object.keys(todos).length) }}
              </span>
            </div>
            <todo-card />
          </div>

          <home-buy-leads-mobile
            v-if="isMobile && showBuyLeads"
            class="col-12 order-2 order-md-3"
            :buy-leads-rows="buyLeadsRows"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarketplaceLeadsTableLite from "@/components/table/MarketplaceLeadsTableLite";
import HomeBuyLeadsMobile from "@/components/section/HomepageMobileBuyLeads";
import HomepageNotifications from "@/components/pane/HomepageNotifications";
import NotificationAccordion from "@/components/accordion/NotificationAccordion";
import TodoCard from "@/components/section/Todo";
import * as MUTATIONS from "@/store/mutation-types";
import Spinner from "@/components/animation/Spinner";
import userDevice from "@/imports/mixins/userDevice";
import HomeMessageBlock from "@/components/section/HomeMessageBlock";
import gaEvent from "@/imports/mixins/gaEvent";
import emailValidation from "@/imports/mixins/emailValidation";
import dates from "@/services/date";
import companyAccount from "@/imports/mixins/companyAccount";

const BUY_LEADS_ROWS = 7;

export default {
  name: 'Home',
  components: {
    HomeMessageBlock,
    Spinner, HomepageNotifications, NotificationAccordion, MarketplaceLeadsTableLite, HomeBuyLeadsMobile, TodoCard},
  mixins: [userDevice, gaEvent, emailValidation, companyAccount],
  data() {
    return {
      displayJumbo: true,
      reviewRating: null,
      reviewCount: null,
      notificationInterval: null,
    }
  },
  computed: {
    buyLeadsRows() {
      if (this.$refs.buyLeadsCard === undefined || this.$refs.buyLeadsFooter === undefined) {
        return BUY_LEADS_ROWS;
      }
      const availableHeight = this.$refs.buyLeadsCard.offsetHeight - this.$refs.buyLeadsFooter.offsetHeight - this.$refs.buyLeadsTable.footerHeight;
      return Math.min(10, Math.max(3, Math.floor(availableHeight / this.$refs.buyLeadsTable.rowHeight)));
    },
    accountBalance() {
      return this.$store.state.company.ACCOUNT_BALANCE;
    },
    todos() {
      return this.$store.state.TODOS;
    },
    company: function() {
      return this.$store.state.company.COMPANY;
    },
    companyUser: function() {
      if (this.company === null) {
        return {};
      }
      const {company_user} = this.company;
      return company_user;
    },
    companyStatus: function() {
      if (this.company === null) {
        return {};
      }
      const {status} = this.company;
      return status;
    },
    companyCreated: function() {
      if (this.company === null) {
        return null;
      }
      const {created_date} = this.company;
      return created_date;
    },
    hasTodo: function() {
      return Object.keys(this.todos).length > 0;
    },
    noticesBusy: {
      get: function() {
        return this.$store.state.NOTIFICATION_BUSY_STATUS;
      },
      set: function(value) {
        this.$store.commit(MUTATIONS.NOTIFICATION_BUSY_STATUS, value);
      }
    },
    reviewRatingClass: function() {
      return this.accountBalance > 0
        ? ['col-md-12', 'col-4', 'col-xl-12', 'mt-md-3', 'mt-xxl-0', 'account__reviews']
        : ['account__settle', 'col-4', 'col-md-12', 'last']
    },
  },
  methods: {
    onSendToken() {
      if (this.lastValidationSentAt === null || dates.pastDateTime(dates.addTime(dates.parseISOString(this.lastValidationSentAt), {minutes: 30}))) {
        this.$refs.homeMessages.toggleValidationSuccess(false);
        this.$api.verification.sendValidationEmail().then(() => {
          this.$refs.homeMessages.toggleValidationSuccess(true);
          this.lastValidationSentAt = dates.isoString();
        });
      } else {
        this.timeLeftTillCanSendValidation = dates.timeToNow(dates.addTime(dates.parseISOString(this.lastValidationSentAt), {minutes: 30}));
      }
    },
    sendRegistrationEvent: function() {
      this.fireEvent(null, 'registration', 'account', 'click');
    },
    scrollToHash() {
      switch (this.$route.hash) {
        case '#todo':
          if (this.$refs.todoList) {
            this.$nextTick(() => this.$refs.todoList.scrollIntoView());
          }
          break;
      }
    },
  },
  created: function() {
    this.$store.dispatch('getNotifications').then(() => {
      this.notificationInterval = setInterval(() => {
        this.$store.dispatch('getNotifications');
      }, 60000);
    });
    this.$api.verification.hasValidated().then((response) => {
      this.requiresEmailValidation = !response.data.data.isValidated;
      this.lastValidationSentAt = response.data.data.lastValidationSentAt;
    });
    this.$api.feedback.companyStats().then((response) => {
      const {average} = response.data.data;
      this.reviewRating = parseFloat(average.rating ?? '0.0') / 2;
      this.reviewCount = average.countPublished ?? 0;
    });
  },
  mounted: function() {
    this.scrollToHash();
    if (this.$route.params.fromPage && this.$route.params.fromPage === 'registration') {
      this.sendRegistrationEvent();
    }
  },
  beforeDestroy: function() {
    clearInterval(this.notificationInterval);
  }
}
</script>

<style scoped lang="scss">

  ::v-deep .b-rating {
    font-size: $h4-font-size;
    height: 20px;
    max-width: 115px;
    .b-rating-star {
      padding: 0 1px;
    }
  }

  .account__overview {
    .account__balance--text,
    .account__review--text {
      font-size: 0.75rem;
    }
  }

  @media only screen and (max-width: 1780px) {
    .account__settle {
      text-align: center;
    }
  }

  @media only screen and (min-width: 1780px) {
    .account__settle {
      overflow: hidden;
      position: relative;

      &:after {
        content: '\200C';
        width: 1px;
        position: absolute;
        right: 5px;
        top: 0;
        background: #e8ecf3;
        height: 100%;
      }
      &.last {

        &:after {
          content: none;
        }
      }
    }
  }

  @include media-breakpoint-down(sm) {
    .account__overview {
      .account__balance--text {
        font-size: $font-size-12;
      }
    }

    .account__balance {
      &--text {
        font-size: .575rem;
      }

      &--currency {
        font-size: $font-size-13;
        .account__balance--text {
          font-size: $font-size-10;
        }
      }
    }

    .account {
      &__settle {

        .btn {
          font-size: $font-size-13;
        }
      }
    }

    .account__reviews {
      padding-left: 0;
      .h4 {
        font-size: $font-size-12;
        span {
          font-size: $font-size-10;
        }
      }
    }
  }

  @media only screen and (max-width: 369px) {
    .account__balance,
    .account__settle {
      flex: 0 0 50%;
      max-width: 50%;
    }

    .account__reviews {
      padding-left: 15px;
      padding-top: 15px;
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  @media only screen and (min-width: 1400px) and (max-width: 1780px) {
    .account__balance,
    .account__settle {
      max-width: 50%;
      flex: 0 0 50%;
    }
    .account__reviews {
      max-width: 100%;
      flex: 0 0 100%;
    }

    .account__balance {
      &--text {
        font-size: $font-size-13;
      }
    }
  }

  @media only screen and (min-width: 1200px) and (max-width: 1250px) {
    ::v-deep .table {
      .qualification-method {
        div {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 30px;
        }
      }
    }
  }

  @media only screen and (min-width: 1200px) and (max-width: 1500px) {
    ::v-deep .table {
      th,
      td {
        padding: .8rem .513rem;
      }
    }
  }

  @media only screen and (min-width: 1500px) {
    ::v-deep table thead .first-col div {
      padding-left: 1rem;
    }
  }

.welcome-container {
  p {
    font-size: $font-size-14;
    margin-bottom: .5rem;
  }
}

</style>
